import React, { useEffect, useState } from 'react';
import { ref, get } from 'firebase/database';
import { database } from './firebaseConfig';
import { MAIN_CARD_ID } from './cardsStore';
import { cancelTransfer } from './transferStore';
import { saveCardContact, removeContact } from './contactsStore';
import {
  generateReceiptNumber, generateIban, maskCardNumber, amountToWordsUA, formatReceiptDate,
  formatReceiptDateTime, generateAuthCode, generateCheckToken, saveReceipt,
  CHECK_ORIGIN, CHECK_DISPLAY_HOST,
} from './receiptUtils';
import { hasPremium } from './subscription';
import './TransferSuccessScreen.css';

const maskCard = (n) => `${n.slice(0, 4)} ${n.slice(4, 6)}** **** ${n.slice(12)}`;

// Екран «Платіж надіслано». transfer — результат sendTransfer; cards —
// картки користувача (для номера/IBAN картки-джерела в квитанції).
// onDone — закрити весь переказ (і після скасування теж).
export default function TransferSuccessScreen({ transfer, cards, onDone }) {
  const { chatId, recipient, cardNumber, value, tx } = transfer;
  const [saved, setSaved] = useState(null); // id збереженого контакту
  const [savingCard, setSavingCard] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [premium, setPremium] = useState(false); // посилання на квитанцію — лише з Преміумом

  const token = generateCheckToken(tx.id);
  const receiptUrl = `${CHECK_ORIGIN}/p/${token}`;
  const recipientLabel = recipient ? recipient.name : maskCard(cardNumber);

  // Смуга під home-індикатором — у колір екрана
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#121212';
    return () => { document.body.style.backgroundColor = prev; };
  }, []);

  // Квитанція за посиланням — ті самі поля, що пише TransactionsDetails
  useEffect(() => {
    let cancelled = false;
    get(ref(database, `users/${chatId}`)).then((snap) => {
      const user = snap.val() || {};
      if (cancelled || !hasPremium(user)) return;
      setPremium(true);
      const card = transfer.fromCardId === MAIN_CARD_ID ? null : cards.find((c) => c.id === transfer.fromCardId);
      saveReceipt(token, {
        receiptNumber: generateReceiptNumber(tx.id),
        date: formatReceiptDate(tx.date),
        senderName: user.pib,
        senderIban: card?.iban || user.iban || generateIban(chatId),
        senderCard: maskCardNumber(card?.number || user.cardNumber),
        recipientName: recipient ? recipient.name : undefined,
        recipientBank: recipient ? 'Універсал Банк' : 'ПриватБанк',
        recipientSystem: 'VISA',
        recipientCard: cardNumber,
        amount: value,
        commission: 0,
        amountWords: amountToWordsUA(tx.amount),
        dateTime: formatReceiptDateTime(tx.date),
        authCode: generateAuthCode(tx.id),
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function copyLink() {
    if (!premium || !navigator.clipboard) return;
    navigator.clipboard.writeText(receiptUrl).catch(() => {});
  }

  function share() {
    if (!premium) return;
    if (navigator.share) navigator.share({ title: 'Квитанція', url: receiptUrl }).catch(() => {});
    else copyLink();
  }

  async function toggleSave() {
    if (savingCard) return;
    setSavingCard(true);
    try {
      if (saved) {
        await removeContact(chatId, saved);
        setSaved(null);
      } else {
        setSaved(await saveCardContact(chatId, {
          name: recipientLabel, cardNumber, photo: recipient?.photo, recipient,
        }));
      }
    } catch (e) { /* лишаємо як було */ }
    setSavingCard(false);
  }

  async function cancel() {
    if (cancelling || !window.confirm('Скасувати платіж?')) return;
    setCancelling(true);
    try {
      await cancelTransfer(transfer);
      if (saved) await removeContact(chatId, saved).catch(() => {});
      onDone();
    } catch (e) {
      setCancelling(false);
      window.alert('Не вдалося скасувати платіж. Спробуйте ще раз');
    }
  }

  return (
    <div className="tss-root">
      <div className="tss-scroll">
        <img className="tss-cat" src="/transfer/success-cat.png" alt="" />
        <h1 className="tss-title">Платіж надіслано</h1>

        <div className="tss-row">
          <img className="tss-row-icon" src="/transfer/card-sent.png" alt="" />
          <div className="tss-row-text">
            <p className="tss-sum"><b>{value.toFixed(2)} ₴</b> на картку</p>
            <p className="tss-to">{recipientLabel}</p>
          </div>
        </div>

        <div className="tss-receipt">
          <div className="tss-receipt-row" onClick={copyLink}>
            <span className="tss-link-icon">🔗</span>
            <div className="tss-receipt-text">
              <p className="tss-receipt-title">Посилання на квитанцію</p>
              <p className="tss-receipt-url">
                <img src="/copy-icon.webp" alt="" />
                <span>{`${CHECK_DISPLAY_HOST}/p/${token}`}</span>
              </p>
            </div>
          </div>
          <button type="button" className="tss-share" onClick={share}>
            <img src="/share-payment.webp" alt="" />
            Поділитись
          </button>
        </div>

        <div className="tss-divider" />

        <div className="tss-save">
          <span className="tss-save-icon">👋</span>
          <p className="tss-save-label">Зберегти картку</p>
          <button
            type="button"
            className={`tss-switch ${saved ? 'on' : ''}`}
            onClick={toggleSave}
            aria-pressed={!!saved}
            aria-label="Зберегти картку"
          >
            <span className="tss-switch-knob" />
          </button>
        </div>
      </div>

      <div className="tss-bottom">
        <button type="button" className="tss-cancel" onClick={cancel} disabled={cancelling}>
          Скасувати платіж
        </button>
        <button type="button" className="tss-done" onClick={onDone}>Готово</button>
      </div>
    </div>
  );
}
